import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiArchive, FiCheckCircle, FiCopy, FiLayers, FiPlus, FiZap } from 'react-icons/fi';
import MyBundlingPackage from './MyBundlingPackage';
import BrowseInventory from './BrowseInventory';
import PlatformIntegration from './PlatformIntegration';
import TransactionMonitor from './TransactionMonitor';
import { useAuth } from '../context/AuthContext';
import PageHeader from '../components/bundling/PageHeader';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const PACKAGE_TABS = [
  { key: 'my-packages', label: 'My Products', path: '/admin/package-manager/my-packages' },
  { key: 'product-catalog', label: 'Product Catalog', path: '/admin/package-manager/product-catalog' },
  { key: 'templates', label: 'Templates', path: '/admin/package-manager/templates' },
  { key: 'drafts-readiness', label: 'Drafts & Readiness', path: '/admin/package-manager/drafts-readiness' },
  { key: 'integrations', label: 'Integrasi API Mitra', path: '/admin/package-manager/integrations' },
  { key: 'channel-transactions', label: 'Monitor Transaksi API', path: '/admin/package-manager/channel-transactions' },
];

const getActiveTabIndex = (pathname) => {
  const activeIndex = PACKAGE_TABS.findIndex((tab) => pathname.includes(`/package-manager/${tab.key}`));
  return activeIndex >= 0 ? activeIndex : 0;
};

const TEMPLATE_LIBRARY = [
  {
    id: 'weekly-highlights',
    title: 'Weekly Highlights',
    description: 'Template untuk promo mingguan dengan durasi 7 hari.',
    defaults: { description: 'Weekly highlight package.', quota: '500', channelHint: 'all-channels' },
  },
  {
    id: 'ieem-priority-campaign',
    title: 'IEEM Priority Campaign',
    description: 'Template campaign dengan prioritas channel IEEM dan readiness hint lengkap.',
    defaults: { description: 'IEEM focused package campaign.', quota: '1000', channelHint: 'ieem-priority' },
  },
  {
    id: 'seasonal-cross-domain',
    title: 'Seasonal Cross-domain',
    description: 'Template seasonal untuk kolaborasi lintas domain/member.',
    defaults: { description: 'Seasonal cross-domain campaign package.', quota: '800', channelHint: 'all-channels' },
  },
];

const TemplatesLibrary = ({ onUseTemplate }) => (
  <div className="flex flex-col gap-4">
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
      {TEMPLATE_LIBRARY.map((template) => (
        <Card
          key={template.id}
          className="rounded-lg border border-[rgba(15,23,42,0.1)] bg-surface-grouped shadow-primeSm"
        >
          <CardContent className="flex flex-col gap-3 pt-6">
            <div className="flex items-center gap-2 text-[#0A84FF]">
              <FiLayers className="h-4 w-4" />
              <span className="font-extrabold">{template.title}</span>
            </div>
            <p className="text-sm leading-relaxed text-label-secondary">{template.description}</p>
            <Button size="sm" className="w-fit gap-2 bg-brand-500 hover:bg-brand-600" onClick={() => onUseTemplate(template)}>
              <FiZap className="h-4 w-4" />
              Use Template
            </Button>
          </CardContent>
        </Card>
      ))}
    </div>
    <p className="text-xs text-label-tertiary">
      Template akan membuka Bundling Wizard dengan prefill awal agar workflow repeat package lebih cepat.
    </p>
  </div>
);

const DraftsReadinessPlaceholder = ({ onManagePackages }) => (
  <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
    <Card className="rounded-lg border border-[rgba(15,23,42,0.1)] bg-surface-grouped shadow-primeSm">
      <CardContent className="flex flex-col gap-3 pt-6">
        <div className="flex items-center gap-2 text-[#0A84FF]">
          <FiArchive className="h-4 w-4" />
          <span className="font-extrabold">Draft Queue</span>
        </div>
        <p className="text-sm leading-relaxed text-label-secondary">
          Area ini disiapkan untuk melihat draft package, blocker, dan resume action dalam satu antrian.
        </p>
        <Button size="sm" className="w-fit gap-2 bg-brand-500 hover:bg-brand-600" onClick={onManagePackages}>
          <FiPlus className="h-4 w-4" />
          Kelola Package
        </Button>
      </CardContent>
    </Card>
    <Card className="rounded-lg border border-[rgba(15,23,42,0.1)] bg-surface-grouped shadow-primeSm">
      <CardContent className="flex flex-col gap-3 pt-6">
        <div className="flex items-center gap-2 text-green-600">
          <FiCheckCircle className="h-4 w-4" />
          <span className="font-extrabold">Readiness Gate</span>
        </div>
        <p className="text-sm leading-relaxed text-label-secondary">
          Sprint 3 memperkenalkan route-nya dulu. Logic readiness lintas package akan diperdalam saat Bundling Wizard masuk Sprint 4-5.
        </p>
      </CardContent>
    </Card>
  </div>
);

const PackageManager = () => {
  const { user, selectedRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const activeIndex = getActiveTabIndex(location.pathname);
  const activeKey = PACKAGE_TABS[activeIndex]?.key || 'my-packages';
  const roleLabel = selectedRole || user?.role || 'operator';

  const handleTabChange = (value) => {
    const idx = Number.parseInt(value, 10);
    if (!Number.isNaN(idx) && PACKAGE_TABS[idx]) {
      navigate(PACKAGE_TABS[idx].path);
    }
  };

  const handleUseTemplate = (template) => {
    localStorage.setItem(
      'packageTemplateDraft',
      JSON.stringify({
        id: template.id,
        title: template.title,
        defaults: template.defaults,
      })
    );
    navigate('/admin/package-manager/my-packages');
  };

  return (
    <div className="flex flex-col gap-5">
      <div>
        <PageHeader
          eyebrow="Package Manager"
          title="Bundling-first workspace"
          description="Satu tempat untuk mengelola package, memilih produk dari catalog, menyiapkan template, dan memantau readiness."
          counts={[{ label: 'Active role', value: String(roleLabel) }]}
          primaryCtaLabel="New Package"
          onPrimaryCta={() => navigate('/admin/package-manager/my-packages')}
          secondaryActions={
            <>
              <Button size="sm" variant="outline" className="gap-2" onClick={() => navigate('/admin/package-manager/templates')}>
                <FiCopy className="h-4 w-4" />
                Use Template
              </Button>
              <Button size="sm" variant="outline" className="gap-2" onClick={() => navigate('/admin/package-manager/drafts-readiness')}>
                <FiArchive className="h-4 w-4" />
                Draft Queue
              </Button>
            </>
          }
        />
      </div>

      <Tabs value={String(activeIndex)} onValueChange={handleTabChange}>
        <TabsList className="h-auto w-full flex-wrap justify-start gap-1 overflow-x-auto py-1">
          {PACKAGE_TABS.map((tab, i) => (
            <TabsTrigger key={tab.key} value={String(i)} className="min-h-10 whitespace-nowrap">
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {activeKey === 'my-packages' && <MyBundlingPackage />}
      {activeKey === 'product-catalog' && <BrowseInventory />}
      {activeKey === 'templates' && <TemplatesLibrary onUseTemplate={handleUseTemplate} />}
      {activeKey === 'drafts-readiness' && (
        <DraftsReadinessPlaceholder onManagePackages={() => navigate('/admin/package-manager/my-packages')} />
      )}
      {activeKey === 'integrations' && <PlatformIntegration />}
      {activeKey === 'channel-transactions' && <TransactionMonitor />}
    </div>
  );
};

export default PackageManager;
